import type {CourseExpandedCommon} from '../../../api/course';
import type {
  EditionApiModel,
  EditionExpandedCommon,
  ProgramApiModel,
  ProgramExpandedCommon,
} from '../../../api/program';

import {programStore} from '../../../api/program';
import {sortCoursesFn} from '../../../utils/entity/course';
import {logger} from '../../../utils/logger';

type Config = {
  coachId: string;
  programId: string;
};

type ExpandedEdition = EditionApiModel & {
  courses: CourseExpandedCommon[];
};

type ExpandedProgram = ProgramApiModel & {
  editions: ExpandedEdition[];
};

const ALLOWED_STATUSES: CourseExpandedCommon['status'][] = [
  'planned',
  'ongoing',
];

export async function courseFetchListPublic({
  coachId,
  programId,
}: Config): Promise<CourseExpandedCommon[]> {
  try {
    const program = (await programStore.public.fetch(
      {
        _id: programId,
        coach_id: coachId,
        expand: {
          program: [['edition_ids', null, {edition: ['course_ids']}]],
        },
      },
      {withAuth: false},
    )) as ExpandedProgram;

    const {editions} = program;

    if (editions.length === 0) {
      return [];
    }

    const editionsById: Map<string, EditionExpandedCommon> = new Map(
      editions.map(edition => [
        edition._id,
        {...edition, contract: null, materials: [], tasks: []},
      ]),
    );

    const expandedProgram: ProgramExpandedCommon = {
      ...program,
      avatar: null,
      background: null,
      editions: Array.from(editionsById.values()),
      employer: null,
      expanded_coaches: [],
      offer: null,
      user: null,
    };

    return (
      editions
        .filter(({status}) => status === 'active')
        .reduce((courses, edition) => [...courses, ...edition.courses], [])
        .filter(({status}) => ALLOWED_STATUSES.includes(status))
        // We can't expand course in public method, so we need to manually set it
        .map(course => ({
          ...course,
          edition: editionsById.get(course.edition_id) ?? null,
          program: expandedProgram,
        }))
        .sort(sortCoursesFn)
    );
  } catch (error) {
    logger.error(error);

    throw error;
  }
}
